import { getFunctions, httpsCallable } from 'firebase/functions';
import type { BusinessVisibility } from '@/types';
import { createLogger } from '@/utils/debug';

const functions = getFunctions();
const log = createLogger('PartnerAuth');

// ===========================================
// Types
// ===========================================

interface ValidateTokenInput {
  token: string;
}

interface ValidateTokenOutput {
  valid: boolean;
  sessionToken?: string;
  companyId?: string;
  companyName?: string;
  businessVisibility?: BusinessVisibility;
  expiresAt?: string;
  error?: string;
}

interface RefreshSessionInput {
  sessionToken: string;
}

interface RefreshSessionOutput {
  success: boolean;
  sessionToken?: string;
  expiresAt?: string;
  error?: string;
}

// ===========================================
// Partner Session Storage
// ===========================================

const SESSION_TOKEN_KEY = 'partner_session_token';
const SESSION_DATA_KEY = 'partner_session_data';

export interface PartnerSessionData {
  sessionToken: string;
  companyId: string;
  companyName: string;
  businessVisibility: BusinessVisibility;
  expiresAt: Date;
}

/**
 * Store partner session in localStorage
 */
export function storePartnerSession(data: PartnerSessionData): void {
  log.debug('storePartnerSession', { companyId: data.companyId, expiresAt: data.expiresAt.toISOString() });
  localStorage.setItem(SESSION_TOKEN_KEY, data.sessionToken);
  localStorage.setItem(SESSION_DATA_KEY, JSON.stringify({
    ...data,
    expiresAt: data.expiresAt.toISOString(),
  }));
}

/**
 * Get partner session from localStorage
 */
export function getStoredPartnerSession(): PartnerSessionData | null {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  const dataStr = localStorage.getItem(SESSION_DATA_KEY);

  if (!token || !dataStr) {
    log.debug('getStoredPartnerSession: no session found');
    return null;
  }

  try {
    const data = JSON.parse(dataStr);
    log.debug('getStoredPartnerSession: session found', { companyId: data.companyId });
    return {
      ...data,
      expiresAt: new Date(data.expiresAt),
    };
  } catch (err) {
    log.error('getStoredPartnerSession: parse error', err);
    return null;
  }
}

/**
 * Clear partner session from localStorage
 */
export function clearPartnerSession(): void {
  log.info('clearPartnerSession: clearing session');
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_DATA_KEY);
}

/**
 * Check if session token is expired (with 5 min buffer)
 */
export function isSessionExpired(session: PartnerSessionData): boolean {
  const bufferMs = 5 * 60 * 1000; // 5 minutes
  return Date.now() > session.expiresAt.getTime() - bufferMs;
}

// ===========================================
// API Functions
// ===========================================

/**
 * Validate a magic link token and get a session token
 */
export async function validatePartnerToken(token: string): Promise<ValidateTokenOutput> {
  return log.operation('validatePartnerToken', async () => {
    const validateToken = httpsCallable<ValidateTokenInput, ValidateTokenOutput>(
      functions,
      'crm_validatePartnerToken'
    );

    const result = await validateToken({ token });
    log.info('validatePartnerToken result', { valid: result.data.valid, companyId: result.data.companyId });
    return result.data;
  });
}

/**
 * Refresh an expiring session token
 */
export async function refreshPartnerSession(sessionToken: string): Promise<RefreshSessionOutput> {
  return log.operation('refreshPartnerSession', async () => {
    const refresh = httpsCallable<RefreshSessionInput, RefreshSessionOutput>(
      functions,
      'crm_refreshPartnerSession'
    );

    const result = await refresh({ sessionToken });
    log.info('refreshPartnerSession result', { success: result.data.success });
    return result.data;
  });
}

/**
 * Auto-refresh session if it's close to expiring
 * Returns updated session data or null if refresh failed
 */
export async function autoRefreshSession(
  currentSession: PartnerSessionData
): Promise<PartnerSessionData | null> {
  // Only refresh if within 10 minutes of expiry
  const refreshThreshold = 10 * 60 * 1000;
  const timeUntilExpiry = currentSession.expiresAt.getTime() - Date.now();

  if (timeUntilExpiry > refreshThreshold) {
    return currentSession; // No refresh needed
  }

  try {
    const result = await refreshPartnerSession(currentSession.sessionToken);

    if (result.success && result.sessionToken && result.expiresAt) {
      const updatedSession: PartnerSessionData = {
        ...currentSession,
        sessionToken: result.sessionToken,
        expiresAt: new Date(result.expiresAt),
      };

      storePartnerSession(updatedSession);
      log.info('autoRefreshSession: session refreshed successfully');
      return updatedSession;
    }
  } catch (error) {
    log.error('autoRefreshSession: failed to refresh', error);
  }

  return null;
}
